/**
 * Audio notifications for rest timer
 * Uses Web Audio API for reliable playback across devices
 */

let audioContext: AudioContext | null = null;

/**
 * Initialize or get the AudioContext
 * Must be called after a user gesture (click, tap) on mobile
 */
function getAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (err) {
      console.warn('Web Audio API not supported:', err);
      return null;
    }
  }
  
  // Resume context if suspended (required after user interaction)
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  
  return audioContext;
}

/**
 * Play a beep tone using Web Audio API
 */
function playTone(frequency: number, duration: number, volume: number = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    // Apply volume with fade out
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (err) {
    console.warn('Failed to play tone:', err);
  }
}

/**
 * Play countdown beep (3, 2, 1 seconds remaining)
 */
export function playCountdownBeep(secondsRemaining: number): void {
  // Higher pitch as we get closer to 0
  const pitches: Record<number, number> = {
    3: 440, // A4
    2: 523, // C5
    1: 659, // E5
  };
  
  const frequency = pitches[secondsRemaining] || 440;
  playTone(frequency, 0.15, 0.3);
}

/**
 * Play rest complete sound (pleasant chime)
 */
export function playRestComplete(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  try {
    // Play a pleasant two-tone chime
    const notes = [
      { freq: 523, delay: 0 },      // C5
      { freq: 659, delay: 0.15 },   // E5
      { freq: 784, delay: 0.3 },    // G5
    ];
    
    notes.forEach(({ freq, delay }) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      const startTime = ctx.currentTime + delay;
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.4, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.4);
    });
  } catch (err) {
    console.warn('Failed to play completion sound:', err);
  }
}

/**
 * Play a test tone to initialize audio on user interaction
 * Call this on a button press to "unlock" audio on mobile
 */
export function initializeAudio(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  // Play a silent tone to initialize
  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    gainNode.gain.value = 0; // Silent
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.001);
  } catch (err) {
    // Ignore errors
  }
}

/**
 * Check if audio is available
 */
export function isAudioAvailable(): boolean {
  return !!(window.AudioContext || (window as any).webkitAudioContext);
}
